import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useHairstylistProfile } from '../hooks/useHairstylistProfile';
import { useTranslation } from '../hooks/useTranslation';
import AuthModal from './AuthModal';
import LanguageSwitcher from './LanguageSwitcher';
import { PROVIDER_TYPES, PROVIDER_TYPE_LABELS } from '../config/providerTypes';
import styles from './Header.module.css';

const CREATE_BILLING_PORTAL_SESSION_URL =
  'https://us-central1-cheriecoiffer-react.cloudfunctions.net/createBillingPortalSession';

function Header() {
  const { currentUser, logout } = useAuth();
  const { isHairstylist, hairstylistProfile } = useHairstylistProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProviderTypeModal, setShowProviderTypeModal] = useState(false);
  const [unreadConversationsCount, setUnreadConversationsCount] = useState(0);
  const [portalLoading, setPortalLoading] = useState(false);
  const { t, language } = useTranslation();

  const handleManageSubscription = async () => {
    if (!hairstylistProfile?.id || portalLoading) return;
    setPortalLoading(true);
    try {
      const res = await fetch(CREATE_BILLING_PORTAL_SESSION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ existingProfileId: hairstylistProfile.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Manage subscription failed', e);
    }
    setPortalLoading(false);
    setShowUserMenu(false);
    setShowMobileMenu(false);
  };

  const displayName =
    currentUser?.displayName || currentUser?.email?.split('@')[0] || '';
  const accountTypeLabel = isHairstylist && hairstylistProfile?.accountType 
    ? (hairstylistProfile.accountType === 'PRO' ? 'PRO' : 'FREE')
    : '';
  const accountLabel = currentUser 
    ? (isHairstylist 
        ? `${t('header.hairstylistAccount')}${accountTypeLabel ? ` ${accountTypeLabel}` : ''}` 
        : t('header.clientAccount'))
    : '';
  const isAdminUser = currentUser && displayName?.toLowerCase() === 'admin';
  const isPendingPayment = isHairstylist && hairstylistProfile?.paymentStatus === 'pending';

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
      navigate('/');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error logging out:', error);
    }
  };

  // Écouter les conversations ayant des messages utilisateur non lus (badge admin)
  useEffect(() => {
    if (!isAdminUser) {
      setUnreadConversationsCount(0);
      return;
    }

    const conversationsRef = collection(db, 'chatConversations');
    const q = query(conversationsRef, orderBy('updatedAt', 'desc'));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const conversations = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Pour chaque conversation, vérifier s'il existe au moins un message "user" non lu
      const checks = await Promise.all(
        conversations.map(async (conv) => {
          const messagesRef = collection(db, 'chatConversations', conv.id, 'messages');
          const unreadQuery = query(
            messagesRef,
            where('sender', '==', 'user'),
            where('read', '==', false),
          );
          const unreadSnap = await getDocs(unreadQuery);
          return !unreadSnap.empty;
        }),
      );

      const unreadCount = checks.filter(Boolean).length;
      setUnreadConversationsCount(unreadCount);
    });

    return () => unsubscribe();
  }, [isAdminUser]);


  return (
    <>
      <header className={styles.header}>
        <div className={styles.container}>
          {/* Logo à gauche - visible uniquement sur mobile */}
          <Link to="/" className={styles.logo}>
            <img 
              src="/cheriecoiffer_logo2.png" 
              alt="Cherie Coiffer" 
              className={styles.logoImage}
            />
          </Link>

          {/* Navigation desktop avec texte */}
          <nav className={styles.nav}>
            <Link
              to="/"
              className={`${styles.navLink} ${location.pathname === '/' ? styles.active : ''}`}
            >
              {t('nav.home')}
            </Link>
            <button
              type="button"
              className={`${styles.navLink} ${location.pathname.startsWith('/search') || location.pathname.startsWith('/hairstylist') ? styles.active : ''}`}
              onClick={() => {
                if (location.pathname.startsWith('/search')) {
                  // Réinitialiser les filtres et la vue si on est déjà sur la recherche
                  try {
                    sessionStorage.removeItem('searchPageFilters');
                    sessionStorage.removeItem('searchPageSort');
                    sessionStorage.removeItem('searchPageSortOrder');
                    sessionStorage.removeItem('searchPageView');
                    sessionStorage.removeItem('searchPageScrollY');
                    sessionStorage.removeItem('searchPageMarkerSelection');
                  } catch (e) {
                    // ignore storage errors
                  }
                  // Recharger la page de recherche proprement
                  window.location.href = '/search';
                } else {
                  navigate('/search');
                }
              }}
            >
              {t('nav.findHairstylist')}
            </button>
            {(!currentUser || !isHairstylist) && (
              <button
                type="button"
                className={styles.navLink}
                onClick={() => setShowProviderTypeModal(true)}
              >
                {t('header.iAmProvider')}
              </button>
            )}
            {isAdminUser && unreadConversationsCount > 0 && (
              <button
                type="button"
                className={styles.navLink}
                onClick={() => navigate('/admin/chat')}
              >
                {t('header.messages')}
                <span className={styles.unreadBadge}>
                  {unreadConversationsCount > 99 ? '99+' : unreadConversationsCount}
                </span>
              </button>
            )}
          </nav>

          {/* Burger menu pour mobile */}
          <div className={styles.mobileLanguageInline}>
            <LanguageSwitcher compact />
          </div>
          <button
            type="button"
            className={styles.burgerMenu}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Menu"
          >
            <span className={styles.burgerLine}></span>
            <span className={styles.burgerLine}></span>
            <span className={styles.burgerLine}></span>
            {isPendingPayment && <span className={styles.alertBadge}>!</span>}
          </button>

          {/* Navigation mobile - visible sur la même ligne que le burger menu */}
          <nav className={styles.navMobile}>
            <Link
              to="/"
              className={`${styles.navLinkMobile} ${location.pathname === '/' ? styles.active : ''}`}
            >
              {t('nav.home')}
            </Link>
            <button
              type="button"
              className={`${styles.navLinkMobile} ${location.pathname.startsWith('/search') || location.pathname.startsWith('/hairstylist') ? styles.active : ''}`}
              onClick={() => {
                if (location.pathname.startsWith('/search')) {
                  try {
                    sessionStorage.removeItem('searchPageFilters');
                    sessionStorage.removeItem('searchPageSort');
                    sessionStorage.removeItem('searchPageSortOrder');
                    sessionStorage.removeItem('searchPageView');
                    sessionStorage.removeItem('searchPageScrollY');
                    sessionStorage.removeItem('searchPageMarkerSelection');
                  } catch (e) {
                    // ignore
                  }
                  window.location.href = '/search';
                } else {
                  navigate('/search');
                }
              }}
            >
              {t('nav.findHairstylist')}
            </button>
            <Link
              to="/posts"
              className={`${styles.navLinkMobile} ${location.pathname.startsWith('/posts') ? styles.active : ''}`}
            >
              {t('nav.forum')}
            </Link>
          </nav>

          <div className={styles.rightSection}>
            <LanguageSwitcher />
            {currentUser ? (
              <div className={styles.userMenu}>
                <button
                  type="button"
                  className={styles.userButton}
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <span className={styles.userName}>{displayName}</span>
                  <span className={styles.accountType}>{accountLabel}</span>
                  <span className={styles.userIcon}>▼</span>
                  {isPendingPayment && <span className={styles.alertBadge}>!</span>}
                </button>
                {showUserMenu && (
                  <div className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
                    {isHairstylist && hairstylistProfile && (
                      <>
                        <Link
                          to={`/hairstylist/${hairstylistProfile.id}`}
                          className={styles.dropdownItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserMenu(false);
                            navigate(`/hairstylist/${hairstylistProfile.id}`);
                          }}
                        >
                          {t('header.viewProfile')}
                          {isPendingPayment && <span className={styles.alertBadgeSmall}>!</span>}
                        </Link>
                        {hairstylistProfile.accountType === 'PRO' && (
                          <>
                            <Link
                              to="/dashboard"
                              className={styles.dropdownItem}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowUserMenu(false);
                                navigate('/dashboard');
                              }}
                            >
                              <span>📊 {t('header.dashboard')}</span>
                              <span className={styles.proBadge}>PRO</span>
                            </Link>
                            <button
                              type="button"
                              className={styles.dropdownItem}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleManageSubscription();
                              }}
                              disabled={portalLoading}
                            >
                              {portalLoading ? '…' : `⚙️ ${t('header.manageSubscription')}`}
                            </button>
                          </>
                        )}
                        <div className={styles.divider}></div>
                      </>
                    )}
                    {isAdminUser && (
                      <>
                        <Link
                          to="/admin/hairstylists/create"
                          className={styles.dropdownItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserMenu(false);
                            navigate('/admin/hairstylists/create');
                          }}
                        >
                          {t('header.addHairstylist')}
                        </Link>
                        <Link
                          to="/admin/verify-email"
                          className={styles.dropdownItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserMenu(false);
                            navigate('/admin/verify-email');
                          }}
                        >
                          {t('header.verifyEmail')}
                        </Link>
                        <Link
                          to="/admin/pending-emails"
                          className={styles.dropdownItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserMenu(false);
                            navigate('/admin/pending-emails');
                          }}
                        >
                          📧 {t('header.pendingEmails')}
                        </Link>
                        <Link
                          to="/admin/analytics"
                          className={styles.dropdownItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserMenu(false);
                            navigate('/admin/analytics');
                          }}
                        >
                          📊 {t('header.dashboardAnalytics')}
                        </Link>
                        <Link
                          to="/admin/chat"
                          className={styles.dropdownItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserMenu(false);
                            navigate('/admin/chat');
                          }}
                        >
                          {t('header.messages')}
                          {unreadConversationsCount > 0 && (
                            <span className={styles.unreadBadge}>{unreadConversationsCount > 99 ? '99+' : unreadConversationsCount}</span>
                          )}
                        </Link>
                        <div className={styles.divider}></div>
                      </>
                    )}
                    <Link
                      to="/profile/favorites"
                      className={styles.dropdownItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowUserMenu(false);
                        navigate('/profile/favorites');
                      }}
                    >
                      {t('header.favorites')}
                    </Link>
                    <Link
                      to="/profile/reviews"
                      className={styles.dropdownItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowUserMenu(false);
                        navigate('/profile/reviews');
                      }}
                    >
                      {t('header.reviews')}
                    </Link>
                    <Link
                      to="/profile/posts"
                      className={styles.dropdownItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowUserMenu(false);
                        navigate('/profile/posts');
                      }}
                    >
                      {t('header.myPosts')}
                    </Link>
                    <Link
                      to="/profile/favorite-posts"
                      className={styles.dropdownItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowUserMenu(false);
                        navigate('/profile/favorite-posts');
                      }}
                    >
                      {t('header.favoritePosts')}
                    </Link>
                    <Link
                      to="/profile/settings"
                      className={styles.dropdownItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowUserMenu(false);
                        navigate('/profile/settings');
                      }}
                    >
                      {t('header.accountSettings')}
                    </Link>
                    <button
                      type="button"
                      className={styles.dropdownItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLogout();
                      }}
                    >
                      {t('header.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                className={styles.loginBtn}
                onClick={() => setShowAuthModal(true)}
              >
                {t('header.login')}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Menu mobile */}
      {showMobileMenu && (
        <>
          <div
            className={styles.mobileMenuOverlay}
            onClick={() => setShowMobileMenu(false)}
          />
          <div className={styles.mobileMenu}>
            {/* Header utilisateur dans le menu mobile */}
            {currentUser ? (
              <>
                <div className={styles.mobileUserBadge}>
                  <p className={styles.mobileBadgeLabel}>{t('header.connected')}</p>
                  <p className={styles.mobileBadgeName}>{displayName}</p>
                  <p className={styles.mobileBadgeStatus}>{accountLabel}</p>
                </div>
                <div className={styles.mobileDivider}></div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.mobileLoginButton}
                  onClick={() => {
                    setShowAuthModal(true);
                    setShowMobileMenu(false);
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  {t('header.login')}
                </button>
                <div className={styles.mobileDivider}></div>
              </>
            )}
            <div className={styles.mobileMenuLanguage}>
              <LanguageSwitcher />
            </div>
            <nav className={styles.mobileNav}>
              <Link
                to="/"
                className={`${styles.mobileNavLink} ${location.pathname === '/' ? styles.active : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.mobileNavIcon}>
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <path d="M9 22V12h6v10" />
                </svg>
                {t('nav.home')}
              </Link>
              <Link
                to="/search"
                className={`${styles.mobileNavLink} ${location.pathname.startsWith('/search') || location.pathname.startsWith('/hairstylist') ? styles.active : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.mobileNavIcon}>
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                {t('nav.findHairstylist')}
              </Link>
              <Link
                to="/posts"
                className={`${styles.mobileNavLink} ${location.pathname.startsWith('/posts') ? styles.active : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.mobileNavIcon}>
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
                {t('nav.forum')}
              </Link>
            </nav>
            <div className={`${styles.mobileDivider} ${styles.mobileDividerAfterForum}`}></div>
            <div className={styles.mobileUserSection}>
              {currentUser && isHairstylist && hairstylistProfile && (
                <>
                  <button
                    type="button"
                    className={styles.mobileUserLink}
                    onClick={() => {
                      navigate(`/hairstylist/${hairstylistProfile.id}`);
                      setShowMobileMenu(false);
                    }}
                  >
                    {t('header.viewProfile')}
                    {isPendingPayment && <span className={styles.alertBadgeSmall}>!</span>}
                  </button>
                  {hairstylistProfile.accountType === 'PRO' && (
                    <>
                      <button
                        type="button"
                        className={styles.mobileUserLink}
                        onClick={() => {
                          navigate('/dashboard');
                          setShowMobileMenu(false);
                        }}
                      >
                        <span>📊 {t('header.dashboard')}</span>
                        <span className={styles.proBadge}>PRO</span>
                      </button>
                      <button
                        type="button"
                        className={styles.mobileUserLink}
                        onClick={() => handleManageSubscription()}
                        disabled={portalLoading}
                      >
                        {portalLoading ? '…' : `⚙️ ${t('header.manageSubscription')}`}
                      </button>
                    </>
                  )}
                </>
              )}
              {currentUser && isAdminUser && (
                <>
                  <button
                    type="button"
                    className={styles.mobileUserLink}
                    onClick={() => {
                      navigate('/admin/hairstylists/create');
                      setShowMobileMenu(false);
                    }}
                  >
                    {t('header.addHairstylist')}
                  </button>
                  <button
                    type="button"
                    className={styles.mobileUserLink}
                    onClick={() => {
                      navigate('/admin/verify-email');
                      setShowMobileMenu(false);
                    }}
                  >
                    {t('header.verifyEmail')}
                  </button>
                  <button
                    type="button"
                    className={styles.mobileUserLink}
                    onClick={() => {
                      navigate('/admin/pending-emails');
                      setShowMobileMenu(false);
                    }}
                  >
                    📧 {t('header.pendingEmails')}
                  </button>
                  <button
                    type="button"
                    className={styles.mobileUserLink}
                    onClick={() => {
                      navigate('/admin/analytics');
                      setShowMobileMenu(false);
                    }}
                  >
                    📊 {t('header.dashboardAnalytics')}
                  </button>
                  <button
                    type="button"
                    className={styles.mobileUserLink}
                    onClick={() => {
                      navigate('/admin/chat');
                      setShowMobileMenu(false);
                    }}
                  >
                    {t('header.messages')}
                    {unreadConversationsCount > 0 && (
                      <span className={styles.unreadBadge}>{unreadConversationsCount > 99 ? '99+' : unreadConversationsCount}</span>
                    )}
                  </button>
                </>
              )}
              <button
                type="button"
                className={styles.mobileUserLink}
                onClick={() => {
                  if (currentUser) {
                    navigate('/profile/favorites');
                    setShowMobileMenu(false);
                  } else {
                    setShowAuthModal(true);
                    setShowMobileMenu(false);
                  }
                }}
              >
                {t('header.favorites')}
              </button>
              <button
                type="button"
                className={styles.mobileUserLink}
                onClick={() => {
                  if (currentUser) {
                    navigate('/profile/reviews');
                    setShowMobileMenu(false);
                  } else {
                    setShowAuthModal(true);
                    setShowMobileMenu(false);
                  }
                }}
              >
                {t('header.reviews')}
              </button>
              <button
                type="button"
                className={styles.mobileUserLink}
                onClick={() => {
                  if (currentUser) {
                    navigate('/profile/posts');
                    setShowMobileMenu(false);
                  } else {
                    setShowAuthModal(true);
                    setShowMobileMenu(false);
                  }
                }}
              >
                {t('header.myPosts')}
              </button>
              <button
                type="button"
                className={styles.mobileUserLink}
                onClick={() => {
                  if (currentUser) {
                    navigate('/profile/favorite-posts');
                    setShowMobileMenu(false);
                  } else {
                    setShowAuthModal(true);
                    setShowMobileMenu(false);
                  }
                }}
              >
                {t('header.favoritePosts')}
              </button>
              {currentUser && (
                <button
                  type="button"
                  className={styles.mobileUserLink}
                  onClick={() => {
                    navigate('/profile/settings');
                    setShowMobileMenu(false);
                  }}
                >
                  {t('header.accountSettings')}
                </button>
              )}
              {currentUser && (
                <>
                  <div className={styles.mobileDivider}></div>
                  <button
                    type="button"
                    className={styles.mobileUserLink}
                    onClick={() => {
                      handleLogout();
                      setShowMobileMenu(false);
                    }}
                  >
                    {t('header.logout')}
                  </button>
                </>
              )}
              {/* Option "Je suis prestataire" - visible seulement si l'utilisateur n'est pas déjà prestataire */}
              {(!currentUser || !isHairstylist) && (
                <>
                  <div className={styles.mobileDivider}></div>
                  <button
                    type="button"
                    className={styles.mobileProviderLink}
                    onClick={() => {
                      setShowMobileMenu(false);
                      setShowProviderTypeModal(true);
                    }}
                  >
                    {t('header.iAmProvider')}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
      {/* Modal de sélection du type de prestation */}
      {showProviderTypeModal && (
        <>
          <div
            className={styles.providerTypeModalOverlay}
            onClick={() => setShowProviderTypeModal(false)}
          />
          <div className={styles.providerTypeModal}>
            <h2 className={styles.providerTypeModalTitle}>{t('header.chooseProviderType')}</h2>
            <div className={styles.providerTypeModalGrid}>
              <button
                type="button"
                className={styles.providerTypeModalOption}
                onClick={() => {
                  navigate(`/signup/${PROVIDER_TYPES.HAIR}`);
                  setShowProviderTypeModal(false);
                }}
              >
                <img src="/icons/hair.png" alt={PROVIDER_TYPE_LABELS[PROVIDER_TYPES.HAIR][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.HAIR].fr} className={styles.providerTypeModalIcon} />
                <span>{PROVIDER_TYPE_LABELS[PROVIDER_TYPES.HAIR][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.HAIR].fr}</span>
              </button>
              <button
                type="button"
                className={styles.providerTypeModalOption}
                onClick={() => {
                  navigate(`/signup/${PROVIDER_TYPES.NAILS}`);
                  setShowProviderTypeModal(false);
                }}
              >
                <img src="/icons/nails.png" alt={PROVIDER_TYPE_LABELS[PROVIDER_TYPES.NAILS][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.NAILS].fr} className={styles.providerTypeModalIcon} />
                <span>{PROVIDER_TYPE_LABELS[PROVIDER_TYPES.NAILS][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.NAILS].fr}</span>
              </button>
              <button
                type="button"
                className={styles.providerTypeModalOption}
                onClick={() => {
                  navigate(`/signup/${PROVIDER_TYPES.MAKEUP}`);
                  setShowProviderTypeModal(false);
                }}
              >
                <img src="/icons/makeup.png" alt={PROVIDER_TYPE_LABELS[PROVIDER_TYPES.MAKEUP][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.MAKEUP].fr} className={styles.providerTypeModalIcon} />
                <span>{PROVIDER_TYPE_LABELS[PROVIDER_TYPES.MAKEUP][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.MAKEUP].fr}</span>
              </button>
              <button
                type="button"
                className={styles.providerTypeModalOption}
                onClick={() => {
                  navigate(`/signup/${PROVIDER_TYPES.EYELASH}`);
                  setShowProviderTypeModal(false);
                }}
              >
                <img src="/icons/eyelash.png" alt={PROVIDER_TYPE_LABELS[PROVIDER_TYPES.EYELASH][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.EYELASH].fr} className={styles.providerTypeModalIcon} />
                <span>{PROVIDER_TYPE_LABELS[PROVIDER_TYPES.EYELASH][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.EYELASH].fr}</span>
              </button>
              <button
                type="button"
                className={styles.providerTypeModalOption}
                onClick={() => {
                  navigate(`/signup/${PROVIDER_TYPES.VENDOR}`);
                  setShowProviderTypeModal(false);
                }}
              >
                <span className={styles.providerTypeModalIconWrapperVendor}>
                  <img src="/icons/vendor.png" alt={PROVIDER_TYPE_LABELS[PROVIDER_TYPES.VENDOR][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.VENDOR].fr} className={styles.providerTypeModalIcon} />
                </span>
                <span>{PROVIDER_TYPE_LABELS[PROVIDER_TYPES.VENDOR][language] || PROVIDER_TYPE_LABELS[PROVIDER_TYPES.VENDOR].fr}</span>
              </button>
            </div>
            <button
              type="button"
              className={styles.providerTypeModalClose}
              onClick={() => setShowProviderTypeModal(false)}
            >
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {showUserMenu && (
        <div
          className={styles.overlay}
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </>
  );
}

export default Header;
